//* Vue
import type { SetupContext } from "vue";
import { ref, computed, reactive, nextTick } from "vue";
//* 组件属性
import type { UiSimplebarEmits, UiSimplebarProps } from "./component";

export const useComposable = (define: UiSimplebarProps, emits: SetupContext<typeof UiSimplebarEmits>["emit"]) => {
    //* 响应式数据
    const refs = {
        visible: ref<boolean>(false),
    };

    //* 数据
    const vars = {
        observer: <ResizeObserver | undefined>undefined,
    };

    //* 节点
    const nodes = {
        slideway: ref<HTMLElement>(),
        container: ref<HTMLElement>(),
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: () => {
            if (!nodes.container.value) return;
            if (nodes.container.value.scrollHeight <= nodes.container.value.clientHeight) {
                return (refs.visible.value = false);
            }

            refs.visible.value = true;
            nextTick(() => {
                //* 节点初始化
                const doms = {
                    bar: nodes.slideway.value?.querySelector(".ui-simplebar-bar") as HTMLDivElement,
                    content: nodes.container.value?.querySelector(".ui-simplebar-content") as HTMLDivElement,
                    container: nodes.container.value as HTMLDivElement,
                };

                //* 检测是否满足运行条件
                if (!doms.bar || !doms.content || !doms.container) return;
                else {
                    //* 位置初始化
                    doms.bar.style.height = (doms.container.clientHeight / doms.content.clientHeight) * doms.container.clientHeight + "px";
                    doms.bar.style.transform = `translateY(${methods.site(doms)}px)`;
                    //* 绑定滚动事件
                    doms.container.onscroll = (ev: Event) => {
                        //* 设置滚动位置
                        doms.bar.style.transform = `translateY(${methods.site(doms)}px)`;
                        //* 响应滚动事件
                        emits("scroll", ev);
                    };
                }
            });
        },

        //* 拖拽函数
        drag: (ev: MouseEvent) => {
            //* 节点初始化
            const doms = {
                bar: nodes.slideway.value?.querySelector(".ui-simplebar-bar") as HTMLDivElement,
                content: nodes.container.value?.querySelector(".ui-simplebar-content") as HTMLDivElement,
                container: nodes.container.value as HTMLDivElement,
            };

            //* 检测是否满足运行条件
            if (!doms.bar || !doms.content || !doms.container) return;
            else {
                ev.preventDefault(); //* 禁用鼠标选择功能
            }

            //* 数据初始化
            const result = { site: ev.clientY, offset: doms.container?.scrollTop || 0 };
            const offset = (result.offset / doms.content.clientHeight) * doms.container.clientHeight;
            //* 事件回调（抬起）
            const mouseup = () => {
                document.removeEventListener("mouseup", mouseup);
                document.removeEventListener("mousemove", mousemove);
            };

            //* 事件回调（移动）
            const mousemove = (event: MouseEvent) => {
                if (!nodes.slideway.value) return;
                else {
                    //* 位置设置
                    nodes.container.value?.scrollTo({
                        top: (doms.content.clientHeight * (offset + (event.clientY - result.site))) / nodes.slideway.value.clientHeight,
                    });
                }
            };

            document.addEventListener("mouseup", mouseup);
            document.addEventListener("mousemove", mousemove);
        },

        //* 移动函数
        move: (ev: MouseEvent) => {
            //* 节点初始化
            const node = nodes.container.value?.querySelector(".ui-simplebar-content") as HTMLDivElement;
            //* 检测是否满足运行条件
            if (!node || !nodes.slideway.value) return;
            else {
                nodes.container.value?.scrollTo({
                    top: (ev.offsetY / nodes.slideway.value.clientHeight) * node.clientHeight,
                    behavior: "smooth",
                });
            }
        },

        //* 位置处理函数
        site: (node: { bar: HTMLElement; content: HTMLElement }) => {
            if (!nodes.container.value || !nodes.slideway.value) return;
            return Math.min(
                Math.max((nodes.container.value.scrollTop / node.content.clientHeight) * nodes.slideway.value.clientHeight, 0),
                nodes.slideway.value.clientHeight - node.bar.offsetHeight
            );
        },
    };

    //* 属性列表
    const binds = reactive({
        bar: computed(() => {
            return {
                style: {
                    borderWidth: `${define.spacing || 0}px`,
                },
            };
        }),

        container: computed(() => {
            if (!define.height) return {};
            if (typeof define.height === "number") return { style: { "max-height": define.height + "px" } };
            return {
                style: {
                    "max-height": define.height,
                },
            };
        }),
    });

    return { refs, vars, nodes, binds, methods };
};
